import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import UploadFileIcon from '@mui/icons-material/UploadFile';

/**
 * Checks if a value is a valid URL string
 */
const isValidUrl = (value) => {
  if (typeof value !== 'string') return false;
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

/**
 * Determines the initial input type based on the value
 */
const getInitialInputType = (value) => {
  if (!value) return 'file';
  if (typeof value === 'string' && isValidUrl(value)) return 'link';
  return 'file';
};

/**
 * MediaInput Component
 *
 * A reusable component for handling media file uploads with an option to manually enter URLs.
 *
 * Props:
 * - value: File object or URL string
 * - onChange: Callback when value changes (receives File or string URL)
 * - accept: File input accept attribute (e.g., "image/*", "audio/*", "video/*")
 * - label: Label text for the input
 * - mediaType: "image" | "audio" | "video" - determines preview rendering
 * - imagePreviewStyle: Custom styles for image preview container (optional)
 */
export default function MediaInput({
  value,
  onChange,
  accept = '*/*',
  label,
  mediaType = 'image',
  imagePreviewStyle = { height: '200px', width: '200px' }
}) {
  const [inputType, setInputType] = useState(() => getInitialInputType(value));
  const [linkValue, setLinkValue] = useState(typeof value === 'string' ? value : '');

  // Update link value when external value changes (for edit mode)
  useEffect(() => {
    if (typeof value === 'string' && isValidUrl(value)) {
      setLinkValue(value);
      setInputType('link');
    }
  }, [value]);

  // Handle input type toggle - only switch mode, don't clear value
  const handleInputTypeChange = (_, newType) => {
    if (newType !== null) {
      setInputType(newType);
      // When switching to link mode, populate linkValue from current value if it's a URL
      if (newType === 'link' && typeof value === 'string' && isValidUrl(value)) {
        setLinkValue(value);
      }
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onChange(file);
    }
  };

  // Handle link input change
  const handleLinkChange = (e) => {
    const url = e.target.value;
    setLinkValue(url);
    if (url && isValidUrl(url)) {
      onChange(url);
    } else if (!url) {
      onChange(null);
    }
  };

  // Handle delete/clear
  const handleClear = () => {
    onChange(null);
    setLinkValue('');
  };

  // Get the preview URL
  const previewUrl = useMemo(() => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    try {
      return URL.createObjectURL(value);
    } catch {
      return null;
    }
  }, [value]);

  // Render the preview based on media type
  const renderPreview = () => {
    if (!previewUrl) return null;

    if (mediaType === 'image') {
      return (
        <img
          style={{ objectFit: 'cover', height: '100%', width: '100%' }}
          src={previewUrl}
          alt={label || 'preview'}
        />
      );
    }

    if (mediaType === 'audio') {
      return <audio key={previewUrl} src={previewUrl} controls style={{ width: '100%' }} />;
    }

    if (mediaType === 'video') {
      return (
        <video
          key={previewUrl}
          src={previewUrl}
          controls
          style={{ maxHeight: '300px', width: '100%' }}
        />
      );
    }

    return null;
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {/* Header with label and mode toggle */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}>
        <Typography variant="body1">{label}</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <ToggleButtonGroup
            value={inputType}
            exclusive
            onChange={handleInputTypeChange}
            size="small">
            <ToggleButton value="file" aria-label="upload file">
              <UploadFileIcon fontSize="small" sx={{ mr: 0.5 }} />
              File
            </ToggleButton>
            <ToggleButton value="link" aria-label="enter link">
              <LinkIcon fontSize="small" sx={{ mr: 0.5 }} />
              Link
            </ToggleButton>
          </ToggleButtonGroup>
          <IconButton onClick={handleClear} size="small">
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>

      {/* File input mode */}
      {inputType === 'file' && (
        <>
          {mediaType === 'image' ? (
            <Button
              component="label"
              sx={{
                ...imagePreviewStyle,
                backgroundColor: '#C1C1C1',
                padding: 0
              }}>
              {previewUrl && renderPreview()}
              <input accept={accept} type="file" onChange={handleFileChange} hidden />
            </Button>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              <Button fullWidth sx={{ textAlign: 'start' }} variant="outlined" component="label">
                <input accept={accept} type="file" onChange={handleFileChange} hidden />
                Choose a file
              </Button>
              {renderPreview()}
            </Box>
          )}
        </>
      )}

      {/* Link input mode */}
      {inputType === 'link' && (
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter URL (e.g., https://example.com/file.mp3)"
            value={linkValue}
            onChange={handleLinkChange}
            error={linkValue && !isValidUrl(linkValue)}
            helperText={linkValue && !isValidUrl(linkValue) ? 'Please enter a valid URL' : ''}
          />
          {/* Preview for link mode */}
          {previewUrl && (
            <Box>
              {mediaType === 'image' && (
                <Box sx={{ ...imagePreviewStyle, overflow: 'hidden' }}>{renderPreview()}</Box>
              )}
              {mediaType !== 'image' && renderPreview()}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
